import { Select, SelectProps, Spin } from 'antd'
import { debounce } from 'lodash'
import React, {
  ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
  useRef,
  useState,
} from 'react'

export type BaseValueType = {
  key?: string
  label: React.ReactNode
  value: string | number
  [key: string]: any
}
export interface DebounceSelectRef<ValueType extends BaseValueType = any> {
  setSelectOptions: (options: ValueType[]) => void
}

export interface FetchOptionsResult<ValueType = any> {
  options: ValueType[]
  hasMore?: boolean
  totalPages?: number
}

export interface DebounceSelectProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
  fetchOptions: (
    search: string,
    pageNo?: number,
  ) => Promise<FetchOptionsResult<ValueType> | ValueType[]>
  debounceTimeout?: number
  pageSize?: number
}

const DebounceSelect = forwardRef(function DebounceSelect<
  ValueType extends BaseValueType = any,
>(
  {
    fetchOptions,
    debounceTimeout = 800,
    pageSize = 20,
    ...props
  }: DebounceSelectProps<ValueType>,
  ref: ForwardedRef<DebounceSelectRef<ValueType>>,
) {
  const [fetching, setFetching] = useState(false)
  const [options, setOptions] = useState<ValueType[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [loadingMore, setLoadingMore] = useState(false)
  const fetchRef = useRef(0)
  const isComposingRef = useRef(false)
  const lastSearchValueRef = useRef<string>('')

  useImperativeHandle(ref, () => {
    return {
      setSelectOptions: (options: ValueType[]) => {
        setOptions(options)
      },
    }
  }, [])
  const loadOptions = useCallback(
    (value: string, page: number = 1, append: boolean = false) => {
      fetchRef.current += 1
      const fetchId = fetchRef.current
      if (!append) {
        setOptions([])
        setFetching(true)
      } else {
        setLoadingMore(true)
      }

      fetchOptions(value, page)
        .then((result) => {
          if (fetchId !== fetchRef.current) {
            // for fetch callback order
            return
          }

          // 兼容旧版本：如果返回的是数组，直接使用
          const isArrayResult = Array.isArray(result)
          const newOptions = isArrayResult ? result : result.options
          const resultHasMore = isArrayResult
            ? false
            : (result.hasMore ?? false)

          if (append) {
            setOptions((prev) => [...prev, ...newOptions])
            setLoadingMore(false)
          } else {
            setOptions(newOptions)
            setFetching(false)
          }
          setHasMore(resultHasMore)
          setCurrentPage(page)
        })
        .catch(() => {
          if (fetchId === fetchRef.current) {
            setFetching(false)
            setLoadingMore(false)
          }
        })
    },
    [fetchOptions],
  )

  const debounceFetcher = useMemo(() => {
    return debounce(
      (value: string) => loadOptions(value, 1, false),
      debounceTimeout,
    )
  }, [loadOptions, debounceTimeout])
  const handleChange = useCallback(
    (value: ValueType | ValueType[] | undefined, option: unknown) => {
      props.onChange?.(value as any, option as any)
      const isEmpty =
        value === undefined ||
        value === null ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'string' && value === '')

      if (isEmpty) {
        debounceFetcher.cancel()
        fetchRef.current += 1
        setOptions([])
        setFetching(false)
        setHasMore(false)
        setCurrentPage(1)
      }
    },
    [props.onChange, debounceFetcher],
  )
  const onCompositionStart = useCallback(() => {
    isComposingRef.current = true
  }, [])

  const handleSearch = useCallback(
    (val: string) => {
      lastSearchValueRef.current = val
      if (isComposingRef.current) {
        return
      }
      if (val.trim() === '') {
        debounceFetcher.cancel()
        fetchRef.current += 1
        setOptions([])
        setFetching(false)
        setHasMore(false)
        setCurrentPage(1)
        return
      }
      debounceFetcher(val)
    },
    [debounceFetcher],
  )
  const onCompositionEnd = useCallback(
    (e: React.CompositionEvent<HTMLDivElement>) => {
      isComposingRef.current = false
      const maybeInput = e.target as unknown as { value?: unknown }
      if (typeof maybeInput.value === 'string') {
        lastSearchValueRef.current = maybeInput.value
      }
      handleSearch(lastSearchValueRef.current)
    },
    [handleSearch],
  )

  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { target } = e
      const element = target as HTMLElement
      const scrollTop = element.scrollTop
      const scrollHeight = element.scrollHeight
      const clientHeight = element.clientHeight

      // 滚动到底部附近（距离底部20px）时加载更多
      if (
        scrollHeight - scrollTop - clientHeight < 20 &&
        hasMore &&
        !loadingMore &&
        !fetching &&
        lastSearchValueRef.current.trim() !== ''
      ) {
        const nextPage = currentPage + 1
        loadOptions(lastSearchValueRef.current, nextPage, true)
      }
    },
    [hasMore, loadingMore, fetching, currentPage, loadOptions],
  )

  return (
    <div
      onCompositionStart={onCompositionStart}
      onCompositionEnd={onCompositionEnd}
    >
      <Select
        filterOption={false}
        onSearch={handleSearch}
        notFoundContent={
          fetching ? (
            <Spin size="small" />
          ) : loadingMore ? (
            <Spin size="small" />
          ) : null
        }
        getPopupContainer={() => document.body}
        {...props}
        allowClear={props.allowClear ?? true}
        showSearch
        onChange={handleChange}
        options={options}
        onPopupScroll={handlePopupScroll}
        loading={fetching || loadingMore}
      />
    </div>
  )
})

export default DebounceSelect
import { Select, SelectProps, Spin } from 'antd'
import { debounce } from 'lodash'
import React, {
  ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
  useRef,
  useState,
} from 'react'

export type BaseValueType = {
  key?: string
  label: React.ReactNode
  value: string | number
  [key: string]: any
}
export interface DebounceSelectRef<ValueType extends BaseValueType = any> {
  setSelectOptions: (options: ValueType[]) => void
}

export interface FetchOptionsResult<ValueType = any> {
  options: ValueType[]
  hasMore?: boolean
  totalPages?: number
}

export interface DebounceSelectProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
  fetchOptions: (
    search: string,
    pageNo?: number,
  ) => Promise<FetchOptionsResult<ValueType> | ValueType[]>
  debounceTimeout?: number
  pageSize?: number
}

const DebounceSelect = forwardRef(function DebounceSelect<
  ValueType extends BaseValueType = any,
>(
  {
    fetchOptions,
    debounceTimeout = 800,
    pageSize = 20,
    ...props
  }: DebounceSelectProps<ValueType>,
  ref: ForwardedRef<DebounceSelectRef<ValueType>>,
) {
  const [fetching, setFetching] = useState(false)
  const [options, setOptions] = useState<ValueType[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [loadingMore, setLoadingMore] = useState(false)
  const fetchRef = useRef(0)
  const isComposingRef = useRef(false)
  const lastSearchValueRef = useRef<string>('')

  useImperativeHandle(ref, () => {
    return {
      setSelectOptions: (options: ValueType[]) => {
        setOptions(options)
      },
    }
  }, [])
  const loadOptions = useCallback(
    (value: string, page: number = 1, append: boolean = false) => {
      fetchRef.current += 1
      const fetchId = fetchRef.current
      if (!append) {
        setOptions([])
        setFetching(true)
      } else {
        setLoadingMore(true)
      }

      fetchOptions(value, page)
        .then((result) => {
          if (fetchId !== fetchRef.current) {
            // for fetch callback order
            return
          }

          // 兼容旧版本：如果返回的是数组，直接使用
          const isArrayResult = Array.isArray(result)
          const newOptions = isArrayResult ? result : result.options
          const resultHasMore = isArrayResult
            ? false
            : (result.hasMore ?? false)

          if (append) {
            setOptions((prev) => [...prev, ...newOptions])
            setLoadingMore(false)
          } else {
            setOptions(newOptions)
            setFetching(false)
          }
          setHasMore(resultHasMore)
          setCurrentPage(page)
        })
        .catch(() => {
          if (fetchId === fetchRef.current) {
            setFetching(false)
            setLoadingMore(false)
          }
        })
    },
    [fetchOptions],
  )

  const debounceFetcher = useMemo(() => {
    return debounce(
      (value: string) => loadOptions(value, 1, false),
      debounceTimeout,
    )
  }, [loadOptions, debounceTimeout])
  const handleChange = useCallback(
    (value: ValueType | ValueType[] | undefined, option: unknown) => {
      props.onChange?.(value as any, option as any)
      const isEmpty =
        value === undefined ||
        value === null ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'string' && value === '')

      if (isEmpty) {
        debounceFetcher.cancel()
        fetchRef.current += 1
        setOptions([])
        setFetching(false)
        setHasMore(false)
        setCurrentPage(1)
      }
    },
    [props.onChange, debounceFetcher],
  )
  const onCompositionStart = useCallback(() => {
    isComposingRef.current = true
  }, [])

  const handleSearch = useCallback(
    (val: string) => {
      lastSearchValueRef.current = val
      if (isComposingRef.current) {
        return
      }
      if (val.trim() === '') {
        debounceFetcher.cancel()
        fetchRef.current += 1
        setOptions([])
        setFetching(false)
        setHasMore(false)
        setCurrentPage(1)
        return
      }
      debounceFetcher(val)
    },
    [debounceFetcher],
  )
  const onCompositionEnd = useCallback(
    (e: React.CompositionEvent<HTMLDivElement>) => {
      isComposingRef.current = false
      const maybeInput = e.target as unknown as { value?: unknown }
      if (typeof maybeInput.value === 'string') {
        lastSearchValueRef.current = maybeInput.value
      }
      handleSearch(lastSearchValueRef.current)
    },
    [handleSearch],
  )

  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { target } = e
      const element = target as HTMLElement
      const scrollTop = element.scrollTop
      const scrollHeight = element.scrollHeight
      const clientHeight = element.clientHeight

      // 滚动到底部附近（距离底部20px）时加载更多
      if (
        scrollHeight - scrollTop - clientHeight < 20 &&
        hasMore &&
        !loadingMore &&
        !fetching &&
        lastSearchValueRef.current.trim() !== ''
      ) {
        const nextPage = currentPage + 1
        loadOptions(lastSearchValueRef.current, nextPage, true)
      }
    },
    [hasMore, loadingMore, fetching, currentPage, loadOptions],
  )

  return (
    <div
      onCompositionStart={onCompositionStart}
      onCompositionEnd={onCompositionEnd}
    >
      <Select
        filterOption={false}
        onSearch={handleSearch}
        notFoundContent={
          fetching ? (
            <Spin size="small" />
          ) : loadingMore ? (
            <Spin size="small" />
          ) : null
        }
        getPopupContainer={() => document.body}
        {...props}
        allowClear={props.allowClear ?? true}
        showSearch
        onChange={handleChange}
        options={options}
        onPopupScroll={handlePopupScroll}
        loading={fetching || loadingMore}
      />
    </div>
  )
})

export default DebounceSelect
